import React, { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';

// Set worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PdfToWord = () => {
    const [file, setFile] = useState(null);
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState('');
    const [progress, setProgress] = useState(0);
    const fileInputRef = React.useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError('');
        setProgress(0);
    };

    const handleConvert = async (e) => {
        // Blur the button to remove focus state
        if (e && e.target) {
            e.target.blur();
        }

        if (!file) {
            setError('Please select a PDF file first.');
            return;
        }

        if (file.type !== 'application/pdf') {
            setError("Invalid file type. Please upload a PDF file.");
            return;
        }

        setConverting(true);
        setError('');
        setProgress(0);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            const totalPages = pdf.numPages;

            const children = [];

            for (let i = 1; i <= totalPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();

                // Group text items by Y coordinate to approximate lines
                const lines = {};

                textContent.items.forEach(item => {
                    const y = Math.round(item.transform[5]); // Y coordinate
                    if (!lines[y]) {
                        lines[y] = [];
                    }
                    lines[y].push(item.str);
                });

                const textItems = textContent.items.map(item => item.str).join(' ');

                children.push(new Paragraph({
                    children: [new TextRun(textItems)],
                }));

                // Add page break or separator
                children.push(new Paragraph({
                    children: [new TextRun({ text: "", break: 1 })],
                }));

                setProgress(Math.round((i / totalPages) * 100));
            }

            const doc = new Document({
                sections: [{
                    properties: {},
                    children: children,
                }],
            });

            const blob = await Packer.toBlob(doc);
            saveAs(blob, `${file.name.replace('.pdf', '')}.docx`);

            // Clear file selection after successful conversion
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

        } catch (err) {
            console.error(err);
            setError('Error converting file. Please try again.');
        } finally {
            setConverting(false);
            setProgress(0);
        }
    };

    return (
        <div className="converter-section">
            <h2>PDF to Word</h2>

            <div className="input-group">
                <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
                onClick={handleConvert}
                disabled={converting || !file}
                className="convert-btn"
            >
                {converting ? `Converting... ${progress}%` : 'Convert to Word'}
            </button>

            {converting && <div className="loading-indicator">Processing... {progress}%</div>}
        </div>
    );
};

export default PdfToWord;
